/* ==========================================================================
   Visual Filter Row
   --------------------------------------------------------------------------
   Deliberately NOT a filter engine.

   assets/theme.js already ships a working native-filter pipeline:

     FilterSort (the Sort + Filter drawer form)
       -> input event, debounced 500ms
       -> fetch(pathname + '?' + params)
       -> document.dispatchEvent('filter:change', { parsedHTML })
       -> history.pushState
     CollectionGrid listens for 'filter:change' and swaps the product grid.

   So a card click here just clicks the matching checkbox in the drawer. The
   cards and the drawer therefore share one state by construction - there is
   no second source of truth that could drift, and Back/Forward, pasted URLs
   and the drawer all keep working for free.

   This file adds:
     - Shop By tab switching (client side, selections survive a tab change)
     - card / chip / clear-all click handling
     - re-rendering the row from the parsed HTML of a filter change
     - the active-filter count badge on the SORT + FILTER button
     - scroll behaviour after a filter change (section setting)
   ========================================================================== */

(function () {
  'use strict';

  /* Sub-regions swapped on a filter change. Mirrors theme.utils.refreshElements:
     the container nodes survive, only their innerHTML is replaced. That keeps
     the aria-live result-count node alive so it still announces. */
  var REFRESH = [
    '[data-vf-tablist]',
    '[data-vf-panels]',
    '[data-vf-selections-wrap]',
    '[data-vf-count]'
  ];

  var FILTER_INPUT = 'input.filter-value__input--non-color';
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var RANGE_INPUT = '.filter-range__input';

  /* ---------------------------------------------------------------------
     Talking to the drawer
     ------------------------------------------------------------------ */

  /* Matched by property rather than an attribute selector on purpose: filter
     param names contain dots and values can contain quotes, both of which are
     awkward to escape safely inside a selector string. */
  function findFilterInput(name, value) {
    var inputs = document.querySelectorAll('filter-sort ' + FILTER_INPUT);

    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].name === name && inputs[i].value === value) return inputs[i];
    }

    return null;
  }

  /* Clicking the checkbox fires the input event the drawer form listens for. */
  function toggleFilterValue(name, value) {
    var input = findFilterInput(name, value);

    if (!input) return false;

    input.click();
    return true;
  }

  /* Only used when the drawer has no input for a value - e.g. the drawer is
     not on the page at all. A real navigation is slower but always correct. */
  function navigateToggle(name, value) {
    var url = new URL(window.location.href);
    var current = url.searchParams.getAll(name);
    var next = current.indexOf(value) === -1
      ? current.concat([value])
      : current.filter(function (v) { return v !== value; });

    url.searchParams.delete(name);
    next.forEach(function (v) { url.searchParams.append(name, v); });
    url.searchParams.delete('page');

    window.location.assign(url.pathname + url.search);
  }

  function clearPriceInputs() {
    var ranges = document.querySelectorAll('filter-sort ' + RANGE_INPUT);

    ranges.forEach(function (input) { input.value = ''; });
    return ranges;
  }

  function removePriceFilter() {
    var ranges = clearPriceInputs();

    if (!ranges.length) return false;

    ranges[0].dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  /* FilterSort listens for this on document and resets every checked input. */
  function clearAllFilters() {
    clearPriceInputs();
    document.dispatchEvent(new CustomEvent('filter:clearAll'));
  }

  /* ---------------------------------------------------------------------
     Borrowed card images, part two

     The section fills most cards in Liquid, but unpaginated
     `collection.products` is capped at 50 by Shopify. On a 500-product
     collection any value whose products all sit past #50 gets nothing, which
     is why the fallback looked inconsistent.

     So for the cards Liquid could not fill, ask Shopify instead: request the
     product grid section filtered by that one value and take the first image.
     Shopify does the matching, so this is exact - it even handles Search &
     Discovery value grouping, which Liquid cannot see. Kept cheap by running
     only on visible unfilled cards, two at a time, and caching per session.
     ------------------------------------------------------------------ */

  var IMG_CACHE = 'vf-img:';

  function cacheRead(key) {
    try { return window.sessionStorage.getItem(IMG_CACHE + key); } catch (e) { return null; }
  }

  function cacheWrite(key, value) {
    try { window.sessionStorage.setItem(IMG_CACHE + key, value); } catch (e) { /* private mode */ }
  }

  function gridSectionId() {
    var grid = document.querySelector('s-collection-grid[data-id]');

    return grid ? grid.dataset.id : null;
  }

  /* Swaps the text placeholder for the fetched image, in place. */
  function applyBorrowedImage(card, src) {
    if (!src) return;

    var media = card.querySelector('.vf-card__media');

    if (!media || media.querySelector('img')) return;

    var image = document.createElement('img');

    /* Decorative: the card body already carries the title, and the button has
       an aria-label, so alt text here would only be repetition. */
    image.setAttribute('alt', '');
    image.setAttribute('loading', 'lazy');
    image.src = src;

    var placeholder = media.querySelector('.vf-card__media-text');

    if (placeholder) placeholder.remove();

    media.insertBefore(image, media.firstChild);
    card.classList.remove('vf-card--text');
    card.classList.add('vf-card--borrowed');
  }

  function fetchBorrowedImage(card) {
    var name = card.dataset.vfName;
    var value = card.dataset.vfValue;

    if (!name || !value) return Promise.resolve();

    var key = window.location.pathname + '?' + name + '=' + value;
    var cached = cacheRead(key);

    /* '' is a cached miss - remembering it stops us refetching every render. */
    if (cached !== null) {
      applyBorrowedImage(card, cached);
      return Promise.resolve();
    }

    var sectionId = gridSectionId();

    if (!sectionId) return Promise.resolve();

    var url = window.location.pathname
      + '?section_id=' + encodeURIComponent(sectionId)
      + '&' + encodeURIComponent(name) + '=' + encodeURIComponent(value)
      + '&filter.v.availability=1';

    return fetch(url)
      .then(function (response) { return response.ok ? response.text() : ''; })
      .then(function (html) {
        if (!html) return;

        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var found = parsed.querySelector('[js-products-grid] img');
        var src = found ? found.getAttribute('src') || '' : '';

        cacheWrite(key, src);
        applyBorrowedImage(card, src);
      })
      .catch(function () { /* a missing placeholder is not worth surfacing */ });
  }

  /* ---------------------------------------------------------------------
     SORT + FILTER count badge
     ------------------------------------------------------------------ */

  /* Reads the incoming markup, not the live DOM: FilterSort dispatches
     'filter:change' BEFORE it refreshes the drawer, so the live inputs still
     hold the previous state at this point. */
  function updateFilterCount(parsed) {
    var badges = document.querySelectorAll('[js-filter-count]');

    if (!badges.length) return;

    var count = 0;

    if (parsed) {
      /* [checked], not :checked - this is a detached DOMParser document, so the
         content attribute is the only reliable signal. */
      count = parsed.querySelectorAll('filter-sort ' + FILTER_INPUT + '[checked]').length;

      var ranges = parsed.querySelectorAll('filter-sort ' + RANGE_INPUT);

      for (var i = 0; i < ranges.length; i++) {
        if (ranges[i].getAttribute('value')) {
          count += 1;
          break;
        }
      }
    }

    badges.forEach(function (badge) {
      badge.textContent = '(' + count + ')';
      badge.hidden = count === 0;
    });
  }

  /* ---------------------------------------------------------------------
     Scroll behaviour after a filter change
     ------------------------------------------------------------------ */

  /* theme.js ends every filter change with a smooth scrollTo(top). Issuing a
     new instant scroll on the next tick supersedes that animation before it is
     visible, which is cheaper and safer than patching the minified theme. */
  function scrollMode() {
    var row = document.querySelector('visual-filter');

    /* No row on this page means no opinion - leave the theme's behaviour be. */
    if (!row) return 'top';

    return row.dataset.vfScroll || 'stay';
  }

  function headerOffset() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');

    return parseInt(raw, 10) || 48;
  }

  function handleScroll(previousY) {
    var mode = scrollMode();

    if (mode === 'top') return;

    if (mode === 'stay') {
      setTimeout(function () {
        window.scrollTo({ top: previousY, behavior: 'auto' });
      }, 0);
      return;
    }

    /* 'grid' - wait a frame so the swapped grid markup has settled. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var grid = document.querySelector('[js-products-grid]');

        if (!grid) return;

        var top = window.scrollY + grid.getBoundingClientRect().top - headerOffset() - 8;

        window.scrollTo({ top: Math.max(top, 0), behavior: 'auto' });
      });
    });
  }

  /* ---------------------------------------------------------------------
     <visual-filter>
     ------------------------------------------------------------------ */

  class VisualFilter extends HTMLElement {
    connectedCallback() {
      this._group = this.dataset.vfDefaultGroup || '';

      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onFilterChange = this._onFilterChange.bind(this);
      this._onRailScroll = this._onRailScroll.bind(this);
      this._onResize = this._onResize.bind(this);

      /* Delegated, so nothing needs rebinding after a re-render. */
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);

      /* Scroll does not bubble, but it can be caught in the capture phase - so
         one listener covers every rail, including rails created by a later
         re-render. */
      this.addEventListener('scroll', this._onRailScroll, true);

      document.addEventListener('filter:change', this._onFilterChange);
      window.addEventListener('resize', this._onResize);

      this._applyTab(this._group);
    }

    disconnectedCallback() {
      document.removeEventListener('filter:change', this._onFilterChange);
      window.removeEventListener('resize', this._onResize);
    }

    /* --- tabs ---------------------------------------------------------- */

    _tabs() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-vf-tab]'));
    }

    _applyTab(group, focusTab) {
      var tabs = this._tabs();

      if (!tabs.length) return;

      var match = tabs.filter(function (tab) { return tab.dataset.vfTab === group; });
      var active = match.length ? group : tabs[0].dataset.vfTab;

      this._group = active;

      tabs.forEach(function (tab) {
        var on = tab.dataset.vfTab === active;

        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;

        if (on && focusTab) tab.focus();
      });

      this.querySelectorAll('[data-vf-panel]').forEach(function (panel) {
        panel.hidden = panel.dataset.vfPanel !== active;
      });

      /* After unhiding - a hidden rail measures zero and cannot be assessed. */
      this._updateArrows();

      /* Deferred so it never competes with the page's own first paint. */
      var self = this;

      if (this._topUpTimer) clearTimeout(this._topUpTimer);
      this._topUpTimer = setTimeout(function () { self._topUpImages(); }, 350);
    }

    /* --- slider ------------------------------------------------------- */

    /* Each arrow is shown only when there is somewhere to go in that
       direction, so a group that fits shows no arrows at all. */
    _updateArrows() {
      this.querySelectorAll('.vf__rail-wrap').forEach(function (wrap) {
        var rail = wrap.querySelector('[data-vf-scroller]');
        var prev = wrap.querySelector('[data-vf-arrow="prev"]');
        var next = wrap.querySelector('[data-vf-arrow="next"]');

        if (!rail || !prev || !next) return;

        if (!rail.clientWidth) {
          prev.hidden = true;
          next.hidden = true;
          return;
        }

        var max = rail.scrollWidth - rail.clientWidth;
        var scrollable = max > 2;

        /* Centre the row when everything fits; leave a scrollable row aligned
           to the start so nothing becomes unreachable. */
        rail.classList.toggle('vf__rail--fits', !scrollable);

        prev.hidden = !scrollable || rail.scrollLeft <= 2;
        next.hidden = !scrollable || rail.scrollLeft >= max - 2;
      });
    }

    _slide(arrow) {
      var wrap = arrow.closest('.vf__rail-wrap');
      var rail = wrap && wrap.querySelector('[data-vf-scroller]');

      if (!rail) return;

      var direction = arrow.dataset.vfArrow === 'prev' ? -1 : 1;

      rail.scrollBy({
        left: direction * rail.clientWidth,
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
    }

    /* Only visible, still-empty cards, two requests in flight at a time. */
    _topUpImages() {
      if (this.dataset.vfImageFallback === 'false') return;

      var pending = [];

      this.querySelectorAll('.vf-card--text[data-vf-card]').forEach(function (card) {
        var panel = card.closest('[data-vf-panel]');

        if (panel && panel.hidden) return;
        if (card.disabled) return;
        if (card.hasAttribute('data-vf-img-queued')) return;

        card.setAttribute('data-vf-img-queued', '');
        pending.push(card);
      });

      if (!pending.length) return;

      var index = 0;

      function next() {
        if (index >= pending.length) return Promise.resolve();

        return fetchBorrowedImage(pending[index++]).then(next);
      }

      next();
      next();
    }

    _onRailScroll(event) {
      var target = event.target;

      if (!target || !target.matches || !target.matches('[data-vf-scroller]')) return;

      if (this._scrollFrame) return;

      this._scrollFrame = requestAnimationFrame(function () {
        this._scrollFrame = null;
        this._updateArrows();
      }.bind(this));
    }

    _onResize() {
      if (this._resizeFrame) return;

      this._resizeFrame = requestAnimationFrame(function () {
        this._resizeFrame = null;
        this._updateArrows();
      }.bind(this));
    }

    /* --- interaction --------------------------------------------------- */

    _onClick(event) {
      var arrow = event.target.closest('[data-vf-arrow]');

      if (arrow) {
        this._slide(arrow);
        return;
      }

      var tab = event.target.closest('[data-vf-tab]');

      if (tab) {
        this._applyTab(tab.dataset.vfTab);
        return;
      }

      var card = event.target.closest('[data-vf-card]');

      if (card) {
        this._onCardClick(card);
        return;
      }

      var chip = event.target.closest('[data-vf-chip]');

      if (chip) {
        this._onChipClick(chip);
      }

      /* [data-vf-clear-all] is handled on document, because the same control
         also renders inside the product grid's empty state. */
    }

    _onCardClick(card) {
      if (card.disabled || card.getAttribute('aria-disabled') === 'true') return;

      /* Optimistic, so the 500ms debounce does not feel dead. The server
         render that follows is the authority and will correct this. */
      card.setAttribute('aria-pressed', card.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      this.setAttribute('aria-busy', 'true');

      if (!toggleFilterValue(card.dataset.vfName, card.dataset.vfValue)) {
        this.removeAttribute('aria-busy');
        navigateToggle(card.dataset.vfName, card.dataset.vfValue);
      }
    }

    _onChipClick(chip) {
      this.setAttribute('aria-busy', 'true');

      if (chip.hasAttribute('data-vf-price')) {
        if (!removePriceFilter()) this.removeAttribute('aria-busy');
        return;
      }

      if (!toggleFilterValue(chip.dataset.vfName, chip.dataset.vfValue)) {
        this.removeAttribute('aria-busy');
        navigateToggle(chip.dataset.vfName, chip.dataset.vfValue);
      }
    }

    _onKeydown(event) {
      var tab = event.target.closest('[data-vf-tab]');

      if (!tab) return;

      var tabs = this._tabs();
      var index = tabs.indexOf(tab);
      var next = null;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          next = tabs[(index - 1 + tabs.length) % tabs.length];
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          next = tabs[(index + 1) % tabs.length];
          break;
        case 'Home':
          next = tabs[0];
          break;
        case 'End':
          next = tabs[tabs.length - 1];
          break;
        default:
          return;
      }

      event.preventDefault();
      this._applyTab(next.dataset.vfTab, true);
    }

    /* --- re-render ----------------------------------------------------- */

    _onFilterChange(event) {
      var parsed = event.detail && event.detail.parsedHTML;

      if (!parsed) {
        this.removeAttribute('aria-busy');
        return;
      }

      var id = this.dataset.vfSectionId;
      var source = (id && parsed.querySelector('visual-filter[data-vf-section-id="' + id + '"]'))
        || parsed.querySelector('visual-filter');

      if (!source) {
        this.removeAttribute('aria-busy');
        return;
      }

      var hasRegions = this.querySelector('[data-vf-panels]') && source.querySelector('[data-vf-panels]');

      if (hasRegions) {
        REFRESH.forEach(function (selector) {
          var from = source.querySelector(selector);
          var to = this.querySelector(selector);

          if (from && to) to.innerHTML = from.innerHTML;
        }, this);
      } else {
        /* The row appeared or disappeared entirely - swap the body, never the
           whole element. The bar sits outside it and holds the drawer trigger,
           which s-drawer bound once at page load with a global query. */
        var body = this.querySelector('[data-vf-body]');
        var incoming = source.querySelector('[data-vf-body]');

        if (body && incoming) body.innerHTML = incoming.innerHTML;
      }

      this._applyTab(this._group);
      this.removeAttribute('aria-busy');
    }
  }

  if (!window.customElements.get('visual-filter')) {
    window.customElements.define('visual-filter', VisualFilter);
  }

  /* ---------------------------------------------------------------------
     Document-level wiring (runs once, independent of the section)
     ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var clear = event.target.closest('[data-vf-clear-all]');

    if (!clear) return;

    event.preventDefault();

    var row = document.querySelector('visual-filter');

    if (row) row.setAttribute('aria-busy', 'true');

    clearAllFilters();
  });

  document.addEventListener('filter:change', function (event) {
    /* Read before anything scrolls: theme.js scrolls only after this dispatch. */
    var previousY = window.scrollY;

    updateFilterCount(event.detail && event.detail.parsedHTML);
    handleScroll(previousY);
  });
})();
